import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ShieldCheck, Award, TrendingUp } from "lucide-react";

const STATUS_STYLE = {
  not_assessed: "cad-chip",
  gap_identified: "cad-chip cad-chip-red",
  planned: "cad-chip cad-chip-gold",
  in_development: "cad-chip cad-chip-blue",
  implemented: "cad-chip cad-chip-green",
  tested: "cad-chip cad-chip-green",
  independently_verified: "cad-chip cad-chip-green",
  operationally_proven: "cad-chip cad-chip-green",
  superior_demonstrated: "cad-chip cad-chip-gold",
};

export const CapabilityRegister = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["capability-register"],
    queryFn: async () => (await api.get("/capability-register")).data,
  });
  const [area, setArea] = useState("");
  const items = useMemo(() => {
    if (!data) return [];
    return area ? data.items.filter((i) => i.area === area) : data.items;
  }, [data, area]);

  const areas = data ? Array.from(new Set(data.items.map((i) => i.area))) : [];

  return (
    <div className="space-y-6" data-testid="page-capability-register">
      <header>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Global Competitive Superiority Programme</div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1 flex items-center gap-2">
          <Award className="text-[#C99A2E]" /> Capability Register
        </h1>
        <div className="text-[13px] text-[#B3C2CC] mt-1 max-w-3xl leading-relaxed">
          Evidence-based tracking of Cadastre360™ capabilities against recognised international benchmarks
          (Landfolio®, truePERMIT™). No superiority claim is displayed without independent verification.
        </div>
      </header>

      {isLoading && <div className="cad-panel p-6 text-[13px] text-[#B3C2CC]">Compiling capability register…</div>}

      {data && (
        <>
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Positioning statement</div>
            <div className="text-[14px] text-[#F6F8FA] mt-2 leading-relaxed">{data.summary.positioning}</div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {Object.entries(data.summary.status_counts).map(([k, v]) => (
              <div key={k} className="cad-panel p-4" data-testid={`status-${k}`}>
                <div className="font-mono-cad text-[10px] uppercase tracking-[0.2em] text-[#7C93A5]">{k.replace(/_/g, " ")}</div>
                <div className="cad-kpi-value text-3xl mt-1 text-[#F6F8FA]">{v}</div>
              </div>
            ))}
          </div>

          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#7C93A5]">Release gates</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
              {data.summary.release_gates.map((g) => (
                <div key={g.gate} className="border cad-hairline rounded-sm p-3">
                  <div className="flex items-center gap-2">
                    <span className="cad-chip cad-chip-gold">Gate {g.gate}</span>
                    <span className="text-[13px] font-display font-bold text-[#F6F8FA]">{g.name}</span>
                  </div>
                  <div className="text-[12px] text-[#B3C2CC] mt-2 leading-relaxed">{g.description}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setArea("")} data-testid="filter-all"
                    className={`cad-chip ${area === "" ? "cad-chip-gold" : ""}`}>All areas</button>
            {areas.map((a) => (
              <button key={a} onClick={() => setArea(a)} data-testid={`filter-${a}`}
                      className={`cad-chip ${area === a ? "cad-chip-gold" : ""}`}>{a}</button>
            ))}
          </div>

          <div className="cad-panel overflow-hidden">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left border-b cad-hairline">
                  {["Area","Capability","Benchmark","Cadastre360","Status","Depth","Gap"].map((h) => (
                    <th key={h} className="px-3 py-2.5 font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="cad-tbody">
                {items.map((r, i) => (
                  <tr key={i} data-testid={`cap-row-${i}`}>
                    <td className="px-3 py-2.5 text-[#F6F8FA] font-mono-cad text-[11px] uppercase tracking-widest">{r.area}</td>
                    <td className="px-3 py-2.5 font-display font-bold text-[#F6F8FA]">{r.capability}</td>
                    <td className="px-3 py-2.5 text-[#B3C2CC]">{r.benchmark}</td>
                    <td className="px-3 py-2.5 text-[#DCE3E8]">{r.cadastre360}</td>
                    <td className="px-3 py-2.5">
                      <span className={STATUS_STYLE[r.status] || "cad-chip"}>{r.status.replace(/_/g, " ")}</span>
                    </td>
                    <td className="px-3 py-2.5 font-mono-cad">
                      <div className="flex items-center gap-2">
                        <div className="w-14 h-1.5 bg-[#18232D] rounded-full overflow-hidden">
                          <div className="h-full" style={{
                            width: `${(r.depth / 5) * 100}%`,
                            background: r.depth >= 4 ? "#6BB88A" : r.depth >= 3 ? "#C99A2E" : "#A96A35",
                          }} />
                        </div>
                        <span>{r.depth}/5</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-[#B3C2CC] max-w-md">{r.gap}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="cad-panel p-5">
            <div className="flex items-center gap-2 font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">
              <ShieldCheck size={12} /> Governance rule
            </div>
            <div className="text-[13px] text-[#B3C2CC] mt-2 leading-relaxed max-w-3xl">
              No item may be marked <span className="cad-chip cad-chip-gold">superior demonstrated</span> without documented
              comparison criteria, verified tests, performance benchmarks and independent expert assessment.
              External superiority claims are reserved for Gate 6 with formal product-governance approval.
            </div>
          </div>
        </>
      )}
    </div>
  );
};
